import { masteruri } from "../../../config";
import { EmergencyForm, formFields } from "../typing/emergencyUpload";

/** Raw answer of the hub: status (0 = no answer) and body text. */
export interface RawAnswer {
    readonly status: number;
    readonly body: string;
}

/**
 * Sends the package to POST api/Updates/emergency as multipart/form-data.
 *
 * XMLHttpRequest, not fetch: fetch reports no upload progress, and a
 * 190 MB upload without a progress bar looks like a hung page. Never
 * rejects - a broken connection comes back as status 0, judged by
 * outcomeOf like every other answer.
 */
export function sendEmergencyUpload(
    token: string,
    form: EmergencyForm,
    file: File,
    onProgress: (loaded: number, total: number) => void,
): Promise<RawAnswer> {
    const data = new FormData();
    formFields(form).forEach(([name, value]) => data.append(name, value));
    data.append("file", file, file.name);

    return new Promise((resolve) => {
        const request = new XMLHttpRequest();
        request.open("POST", masteruri + "/Updates/emergency");
        request.setRequestHeader("Authorization", "Bearer " + token);
        request.upload.onprogress = (event) => onProgress(event.loaded, event.lengthComputable ? event.total : 0);
        request.onload = () => resolve({ status: request.status, body: request.responseText ?? "" });
        request.onerror = () => resolve({ status: 0, body: "" });
        request.onabort = () => resolve({ status: 0, body: "" });
        request.send(data);
    });
}

/**
 * The releases the hub knows (GET api/Updates), for the duplicate check
 * before uploading. An empty list when the hub cannot be asked - the hub
 * checks again anyway.
 */
export async function fetchReleases(token: string): Promise<readonly { software?: string; version?: string }[]> {
    try {
        const response = await fetch(masteruri + "/Updates", { headers: { Authorization: "Bearer " + token } });
        const body = response.ok ? await response.json() : null;
        return Array.isArray(body?.data?.updates) ? body.data.updates : [];
    } catch {
        return [];
    }
}
